<template>
	<InputField
		class="mb-100"
		:class="{ 'hide-default-errors': !hasServerErrors }"
		:field="v$.password"
		field-name="Password"
		data-test="password-input-field"
		@clear-server="serverValidationHandler.clearServerErrors(serverError)"
	>
		<template v-if="showPasswordLabel" #label>{{ label }}</template>
		<template #input>
			<div class="display-flex align-items-center position-relative">
				<input
					v-model="v$.password.$model"
					:aria-label="label"
					:data-test="componentKey"
					name="password"
					autocomplete="new-password"
					class="input"
					:type="maskedPassword ? 'password' : 'text'"
					:placeholder="displayTestVariant ? 'Password' : ''"
				/>
				<BaseSvgIcon
					class="password-icon position-absolute text-color-content-tertiary"
					width="23"
					height="23"
					:name="maskedPassword ? 'view' : 'view-hide'"
					data-test="password-mask-toggle"
					@click="maskToggle = !maskToggle"
				/>
			</div>
			<ul class="mt-50">
				<li v-for="indicator in validationIndicators" :key="indicator.label">
					<ValidationIndicator :label="indicator.label" :valid="indicator.valid" />
				</li>
				<li v-if="exceededMaxLength">
					<ValidationIndicator label="Maximum of 72 characters" :valid="!passwordTooLong" />
				</li>
			</ul>
		</template>
	</InputField>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { maxLength, minLength, required } from '@vuelidate/validators';
import { passwordContainsLetter, passwordContainsNumber } from '@validators/passwordRegex';
import { passwordMaxLength, passwordMinLength } from '@constants/validation';
import InputField from '@components/form/input-field.vue';
import ServerValidationHandler from '@classes/ServerValidationHandler';
import { useVuelidate } from '@vuelidate/core';
import ValidationIndicator from '@components/form/validation-indicator.vue';

interface Props {
	modelValue?: string;
	serverError?: string;
	componentKey?: string;
	serverValidationHandler: ServerValidationHandler;
	showPasswordLabel?: boolean;
	label?: string;
	forceMask?: boolean;
	displayTestVariant?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	modelValue: undefined,
	label: 'Password',
	serverError: 'password',
	componentKey: 'password',
	showPasswordLabel: true,
	forceMask: false,
	displayTestVariant: false
});

interface Emits {
	(e: 'update:modelValue', value: string): void;
}
const emit = defineEmits<Emits>();

const maskToggle = ref(true);
const exceededMaxLength = ref(false);
const state = reactive({ password: '' });

const maskedPassword = computed(() => {
	return maskToggle.value || props.forceMask;
});

const rules = computed(() => {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const validations: any = {
		password: {
			required,
			minLength: minLength(passwordMinLength),
			maxLength: maxLength(passwordMaxLength),
			passwordContainsLetter: passwordContainsLetter(),
			passwordContainsNumber: passwordContainsNumber(),
			serverError: props.serverValidationHandler.serverError(props.serverError)
		}
	};

	return validations;
});

const passwordTooLong = computed(() => state.password.length > passwordMaxLength);

const v$ = useVuelidate(rules, state);

const validationIndicators = computed(() => {
	return [
		{
			label: 'Minimum of 8 characters',
			valid:
				v$.value.$dirty &&
				!v$.value.$errors.some((e) => e.$validator === 'minLength' || e.$validator === 'required')
		},
		{
			label: 'At least one letter',
			valid: v$.value.$dirty && !v$.value.$errors.some((e) => e.$validator === 'passwordContainsLetter')
		},
		{
			label: 'At least one number',
			valid: v$.value.$dirty && !v$.value.$errors.some((e) => e.$validator === 'passwordContainsNumber')
		}
	];
});

const hasServerErrors = computed(() => {
	return v$.value.$errors.some((e) => e.$validator === 'serverError');
});

watch(
	() => state.password,
	(passwordVal: string) => {
		if (passwordTooLong.value) {
			exceededMaxLength.value = true;
		}

		if (!v$.value.$invalid) {
			emit('update:modelValue', passwordVal);
		} else {
			emit('update:modelValue', '');
		}
	},
	{ immediate: true }
);
</script>

<style scoped lang="scss">
@use '../../styles/constants/_colors.scss' as *;

.password-icon {
	right: 13px;

	&:hover,
	&:active {
		cursor: pointer;
	}
}

.hide-default-errors {
	:deep(.field__error-messages) {
		display: none;
	}
}
</style>
